import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { getPassport, getCards } from "@/api/gameApi"
import { DigitalPassport, GameCard } from "@/types/battle"
import { USE_MOCK_BATTLE } from "@/constants/guardians"
import { AFFILIATION_ELEMENT_PASSIVE_LINES } from "@/constants/affiliationInfo"
import { warmGuardianCardAssets } from "@/constants/cardAssets"
import {
  defaultElementStats,
  EMPTY_PVP_STATS,
  isSameGameCardList,
  isSameDigitalPassport,
  mergeDigitalPassport,
  normalizeDigitalPassport,
  syncPassportActiveCard,
} from "@/utils/gameCards"
import { sanitizeActiveCard } from "@/utils/passportDebug"
import { guardiansLogApiError } from "@/utils/guardiansDebugLog"
import {
  enrichPassportCity,
  loadGuardiansCities,
  getCachedGuardiansCities,
} from "./guardiansCity"

const MOCK_PASSPORT: DigitalPassport = {
  user_id: 1,
  nickname: "Тестер",
  publicGameCode: "ХРАНИТЕЛЬ-001",
  coins: 100,
  cardCapacity: 30,
  chakraCharges: 100,
  fuseBoosters: 0,
  styleChangeTokens: 0,
  energy: 80,
  maxEnergy: 100,
  globalPts: 1000,
  localPts: 1000,
  academicLeague: "TITAN",
  affiliationElement: "FIRE",
  avatarVariant: 1,
  affiliationPassive: {
    title: "Пассив · Огонь",
    description: AFFILIATION_ELEMENT_PASSIVE_LINES.FIRE,
  },
  needsAffiliationSelection: false,
  guardianPath: "STRAZH",
  guardianPathInfo: { title: "Страж", description: "Дуэли — моя стихия." },
  needsGuardianPathSelection: false,
  profileFrameKey: null,
  minorityBonusUntil: null,
  pvpStats: { ...EMPTY_PVP_STATS },
  deckPvpStats: { ...EMPTY_PVP_STATS },
  elementStats: defaultElementStats(),
  elementHighlights: {
    favoriteElement: null,
    bestElement: null,
    bestElementMinBattles: 5,
  },
  averageGrade: 8.5,
  academicBoostActiveUntil: null,
  qrBattlesToday: 1,
  activeCard: null,
  city_id: 1,
  city: { city_id: 1, title: "Мачулищи" },
  visibleOnMap: true,
  shardDust: {
    FIRE: 47,
    WATER: 0,
    EARTH: 12,
    ICE: 0,
    WIND: 0,
    LIGHTNING: 0,
  },
}

interface GuardiansPassportContextValue {
  passport: DigitalPassport | null
  loading: boolean
  /** Ошибка сети при стартовой загрузке (не путать с «нет профиля»). */
  loadError: "offline" | null
  retryInitialLoad: () => void
  cardsVersion: number
  /** Последний известный список карт (старт/restart/GET коллекции). */
  collectionCards: GameCard[]
  hasPassport: boolean
  /** Паспорт есть, лига выбрана, в коллекции ≥ 2 карт — можно в основные табы. */
  isGameReady: boolean
  refreshPassport: () => Promise<DigitalPassport | null>
  /** Свежие карты + паспорт с сервера (после прокачки, слияния и т.д.) */
  syncGameState: () => Promise<DigitalPassport | null>
  applyPassport: (
    next: DigitalPassport | unknown,
    bumpCards?: boolean,
    cards?: GameCard[]
  ) => void
  setCollectionCards: (cards: GameCard[]) => void
  clearPassport: () => void
  bumpCards: () => void
}

const GuardiansPassportContext =
  createContext<GuardiansPassportContextValue | null>(null)

export function GuardiansPassportProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [passport, setPassport] = useState<DigitalPassport | null>(null)
  const passportRef = useRef<DigitalPassport | null>(null)
  passportRef.current = passport
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<"offline" | null>(null)
  const [loadNonce, setLoadNonce] = useState(0)
  const [cardsVersion, setCardsVersion] = useState(0)
  const [collectionCards, setCollectionCardsState] = useState<GameCard[]>([])
  const collectionCardsRef = useRef(collectionCards)
  collectionCardsRef.current = collectionCards

  const retryInitialLoad = useCallback(() => {
    setLoadError(null)
    setLoading(true)
    setLoadNonce((n) => n + 1)
  }, [])

  const setCollectionCards = useCallback((cards: GameCard[]) => {
    setCollectionCardsState((prev) =>
      isSameGameCardList(prev, cards) ? prev : cards
    )
  }, [])

  const bumpCards = useCallback(() => {
    setCardsVersion((v) => v + 1)
  }, [])

  const withCityLabel = useCallback((next: DigitalPassport): DigitalPassport => {
    const cities = getCachedGuardiansCities()
    const repaired: DigitalPassport = {
      ...next,
      nickname: next.nickname?.trim() || "Хранитель",
      maxEnergy: Math.max(1, next.maxEnergy || 100),
      activeCard: sanitizeActiveCard(next.activeCard),
    }
    return cities.length ? enrichPassportCity(repaired, cities) : repaired
  }, [])

  const repairPassport = useCallback((next: unknown): DigitalPassport | null => {
    if (!next || typeof next !== "object") return null
    return normalizeDigitalPassport(next)
  }, [])

  const hydrateActiveCard = useCallback(
    async (current: DigitalPassport): Promise<DigitalPassport> => {
      if (!USE_MOCK_BATTLE && !current.academicLeague) {
        return current
      }
      try {
        const cards = await getCards()
        setCollectionCardsState((prev) =>
          isSameGameCardList(prev, cards) ? prev : cards
        )
        let synced = syncPassportActiveCard(current, cards)

        // Legacy: activeCardId битый, в коллекции одна карта — экипируем её на бэке
        if (
          !synced.activeCardId &&
          cards.length === 1 &&
          cards[0].id &&
          !USE_MOCK_BATTLE
        ) {
          try {
            const { setActiveCard } = await import("../../api/gameApi")
            synced = await setActiveCard(cards[0].id)
            synced = syncPassportActiveCard(synced, cards)
          } catch (e) {
            if (__DEV__) {
              console.warn("[PASSPORT] auto set-active-card failed:", e)
            }
            synced = {
              ...synced,
              activeCardId: cards[0].id,
              activeCard: cards[0],
            }
          }
        } else if (
          !synced.activeCardId &&
          cards.length === 1 &&
          cards[0].id &&
          USE_MOCK_BATTLE
        ) {
          synced = { ...synced, activeCardId: cards[0].id, activeCard: cards[0] }
        }

        return synced
      } catch (e) {
        guardiansLogApiError("hydrateActiveCard", e)
        return current
      }
    },
    []
  )

  const applyPassport = useCallback(
    (next: DigitalPassport | unknown, bumpCardsFlag = false, cards?: GameCard[]) => {
      let mergedForHydrate: DigitalPassport | null = null

      setPassport((prev) => {
        const parsed = repairPassport(next)
        if (!parsed) return prev
        const merged =
          prev != null ? mergeDigitalPassport(prev, next) : withCityLabel(parsed)
        mergedForHydrate = withCityLabel(merged)
        return mergedForHydrate
      })

      if (cards?.length) {
        setCollectionCards(cards)
      }
      if (bumpCardsFlag) bumpCards()

      if (mergedForHydrate) {
        void hydrateActiveCard(mergedForHydrate).then((hydrated) => {
          setPassport((prev) => {
            if (!prev) return prev
            const nextPassport = withCityLabel(hydrated)
            return nextPassport === prev ? prev : nextPassport
          })
        })
      }

      if (!getCachedGuardiansCities().length) {
        void loadGuardiansCities().then((cities) => {
          setPassport((prev) =>
            prev ? enrichPassportCity(prev, cities) : prev
          )
        })
      }
    },
    [bumpCards, hydrateActiveCard, repairPassport, setCollectionCards, withCityLabel]
  )

  const clearPassport = useCallback(() => {
    setPassport(null)
    setCollectionCards([])
    bumpCards()
  }, [bumpCards])

  const refreshPassport = useCallback(async () => {
    if (USE_MOCK_BATTLE) {
      setPassport(MOCK_PASSPORT)
      return MOCK_PASSPORT
    }
    const data = await getPassport()
    if (!data) {
      setPassport(null)
      return null
    }
    const cities = await loadGuardiansCities()
    let enriched = enrichPassportCity(withCityLabel(data), cities)
    enriched = await hydrateActiveCard(enriched)
    enriched = cities.length ? enrichPassportCity(enriched, cities) : enriched
    setPassport(enriched)
    return enriched
  }, [hydrateActiveCard, withCityLabel])

  const syncGameState = useCallback(async () => {
    if (USE_MOCK_BATTLE) {
      setPassport((prev) => (prev === MOCK_PASSPORT ? prev : MOCK_PASSPORT))
      return MOCK_PASSPORT
    }

    const [passportData, cards] = await Promise.all([
      getPassport().catch(() => null),
      getCards().catch(() => [] as GameCard[]),
    ])

    const prevCards = collectionCardsRef.current
    const cardsChanged =
      cards.length > 0 && !isSameGameCardList(prevCards, cards)

    if (cardsChanged) {
      setCollectionCards(cards)
    }

    if (!passportData) {
      if (passportRef.current !== null) {
        setPassport(null)
        setCollectionCards([])
        bumpCards()
      }
      return null
    }

    const cities = getCachedGuardiansCities().length
      ? getCachedGuardiansCities()
      : await loadGuardiansCities()
    let enriched = enrichPassportCity(withCityLabel(passportData), cities)
    enriched = syncPassportActiveCard(
      enriched,
      cards.length > 0 ? cards : prevCards
    )
    enriched = cities.length ? enrichPassportCity(enriched, cities) : enriched

    setPassport((prev) => (isSameDigitalPassport(prev, enriched) ? prev : enriched))

    if (cardsChanged) {
      bumpCards()
    }
    return enriched
  }, [bumpCards, setCollectionCards, withCityLabel])

  useEffect(() => {
    warmGuardianCardAssets()
  }, [])

  useEffect(() => {
    let mounted = true
    void (async () => {
      try {
        await loadGuardiansCities()
        if (USE_MOCK_BATTLE) {
          if (mounted) {
            setPassport(MOCK_PASSPORT)
            setLoadError(null)
          }
          return
        }
        const data = await getPassport()
        if (!mounted) return
        if (data) {
          const cards = await getCards().catch(() => [] as GameCard[])
          if (!mounted) return
          setCollectionCardsState(cards)

          const cities = getCachedGuardiansCities()
          let enriched = enrichPassportCity(withCityLabel(data), cities)
          enriched = await hydrateActiveCard(enriched)
          enriched = cities.length ? enrichPassportCity(enriched, cities) : enriched
          if (mounted) {
            setPassport(enriched)
            setLoadError(null)
          }
        } else {
          setPassport(null)
          setCollectionCardsState([])
          setLoadError(null)
        }
      } catch {
        if (mounted) {
          setPassport(null)
          setLoadError("offline")
        }
        guardiansLogApiError("initial passport load", new Error("getPassport failed"))
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [loadNonce])

  const value = useMemo(
    () => ({
      passport,
      loading,
      loadError,
      retryInitialLoad,
      cardsVersion,
      collectionCards,
      hasPassport: !!passport,
      // Достаточно лиги и ≥1 карты: после продажи/слияния можно остаться с одной —
      // не кидаем обратно в онбординг (как при старте с 2 картами).
      isGameReady:
        !!passport?.academicLeague && collectionCards.length >= 1,
      refreshPassport,
      syncGameState,
      applyPassport,
      setCollectionCards,
      clearPassport,
      bumpCards,
    }),
    [
      passport,
      loading,
      loadError,
      retryInitialLoad,
      cardsVersion,
      collectionCards,
      refreshPassport,
      syncGameState,
      applyPassport,
      clearPassport,
      bumpCards,
    ]
  )

  return (
    <GuardiansPassportContext.Provider value={value}>
      {children}
    </GuardiansPassportContext.Provider>
  )
}

export function useGuardiansPassport() {
  const ctx = useContext(GuardiansPassportContext)
  if (!ctx) {
    throw new Error("useGuardiansPassport must be used within GuardiansPassportProvider")
  }
  return ctx
}

export { MOCK_PASSPORT }
